// @flow
import * as React from 'react'
import ReactDOM from 'react-dom'
import requestAnimationFrame from 'mz-utils/requestAnimationFrame'
import { Transition } from 'react-transition-group'

// Types
export type Props = {
  showed: boolean,
  duration: number,
  render: (boolean) => any,
}

// Internalts
let Z_INDEX_COUNTER = 10000
const IS_SERVER = typeof document === 'undefined'

/**
 * This object helps to keep the content behind the lightbox to be non-scrollable,
 * also it scrolls to the top of the lightbox on iOS devices when autocomplet
 * lightbox is opened
 * @type {Object}
 */
const BODY_SCROLL_FIX = {
  lightboxes: new Set(),
  lastScrollPosition: 0,

  add(lightboxId) {
    const prevSize = this.lightboxes.size
    this.lightboxes.add(lightboxId)

    if (!prevSize && this.lightboxes.size > 0) {
      this.lastScrollPosition = document.scrollingElement?.scrollTop || 0
      setTimeout(() => {
        if (!document.body || !document.scrollingElement) return
        document.scrollingElement.scrollTop = 0
        document.body.style.position = 'fixed'
        document.body.style.width = '100%'
        document.body.style.height = '100%'
        document.body.style.top = `-${this.lastScrollPosition}px`
      }, 10)
    }
  },
  remove(lightboxId) {
    const removed = this.lightboxes.delete(lightboxId)
    if (removed && !this.lightboxes.size) {
      requestAnimationFrame(() => {
        if (!document.body || !document.scrollingElement) return
        document.body.style.position = 'static'
        document.scrollingElement.scrollTop = this.lastScrollPosition || 0
        document.body.style.top = '0'
      })
    }
  },
}

/**
 * A wrapper around `render` function that adds animation using
 * Transition component
 * @param       {Props} props
 * @constructor
 */
function TransitionLightbox(props: Props) {
  const lightboxId = React.useMemo(() => Math.random())

  React.useLayoutEffect(() => {
    if (props.showed) {
      BODY_SCROLL_FIX.add(lightboxId)
    } else {
      BODY_SCROLL_FIX.remove(lightboxId)
    }
    return () => {
      BODY_SCROLL_FIX.remove(lightboxId)
    }
  }, [props.showed])

  return (
    <Transition
      in={props.showed}
      timeout={{ enter: 1, exit: props.duration }}
      mountOnEnter
      unmountOnExit
      appear
    >
      {(state) => props.render(state === 'entered')}
    </Transition>
  )
}

/**
 * Component that shows what returned by `render` prop in a portal.
 * The `render` function called with one boolean argument `showed`,
 * which could be used to animate internal content in 650ms (with CSS).
 */
class AnimatedPortal extends React.Component<Props> {
  unmounted = false

  node = null

  hideTimer = null

  componentDidMount() {
    if (IS_SERVER) return

    if (this.node) {
      this.node.style.position = 'relative'
      if (document.body) {
        document.body.appendChild(this.node)
      }
      if (this.props.showed) {
        this.animateIn()
      }
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.showed && nextProps.showed !== this.props.showed) {
      this.animateIn()
    }
  }

  componentWillUnmount() {
    if (this.node) {
      this.unmounted = true
      this.animateOut(this.props)
    }
  }

  animateIn = () => {
    if (this.node) {
      this.node.style.zIndex = `${Z_INDEX_COUNTER++}`
    }
  }

  animateOut = (props: Props) => {
    this.hideTimer = setTimeout(this.maybeUnmountComponent, props.duration)
  }

  maybeUnmountComponent = () => {
    if (IS_SERVER) return

    if (this.unmounted && this.node) {
      if (document.body) {
        document.body.removeChild(this.node)
        this.node = null
      }
    }
  }

  render() {
    if (IS_SERVER) {
      return null
    }
    if (!this.node) {
      this.node = document.createElement('div')
    }
    return ReactDOM.createPortal(
      <TransitionLightbox {...this.props} />,
      this.node
    )
  }
}

export default AnimatedPortal
